import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, ArrowRight, Lock, SkipForward, Wifi, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ImportHoursModal from "@/components/ImportHoursModal";
import BiometricPreviewModal from "@/components/BiometricPreviewModal";
import SkippedRowsModal from "@/components/SkippedRowsModal";
import PayLineDrawer from "@/components/PayLineDrawer";
import EmployeeDrawer from "@/components/EmployeeDrawer";
import PendingBiometricBanner from "@/components/PendingBiometricBanner";
import HistoryDrawer from "@/components/HistoryDrawer";
import WindowExceptionsStrip from "@/components/WindowExceptionsStrip";
import SickPHBanner from "@/components/SickPHBanner";
import WorkflowStrip from "@/components/WorkflowStrip";
import { formatKina, formatHours } from "@/lib/money";
import { PAYRUN, PAYRUN_LOAN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Coalesce debounce PER LINE — every pending field change for the same row is merged into
// one PATCH so we send `{rate_override, hours: {...}}` in a single request instead of racing
// separate PATCHes that each read the pre-change document. Kills the lost-update bug that
// wiped fields on rapid multi-field typing.
function useCoalescedLinePatch(fn, ms = 300) {
    const timers = useRef(new Map());
    const pending = useRef(new Map()); // lineId -> merged patch (with nested hours merge)
    useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);
    return (lineId, patch) => {
        const prev = pending.current.get(lineId) || {};
        const merged = { ...prev, ...patch };
        if (patch.hours) merged.hours = { ...(prev.hours || {}), ...patch.hours };
        pending.current.set(lineId, merged);
        const t = timers.current.get(lineId);
        if (t) clearTimeout(t);
        const handle = setTimeout(() => {
            const p = pending.current.get(lineId);
            timers.current.delete(lineId);
            pending.current.delete(lineId);
            if (p) fn(lineId, p);
        }, ms);
        timers.current.set(lineId, handle);
    };
}

function NumCell({ value, onChange, testid, cols = 6, alignRight = true }) {
    return (
        <input
            data-testid={testid}
            type="number"
            step="0.01"
            min="0"
            value={value ?? 0}
            onChange={(e) => onChange(e.target.valueAsNumber || 0)}
            onFocus={(e) => e.target.select()}
            className={`kp-focus w-full font-mono-money px-2 py-1.5 text-[13px] bg-transparent border border-transparent hover:border-neutral-200 focus:border-[color:var(--kp-red)] focus:bg-white rounded-[2px] ${
                alignRight ? "text-right" : "text-left"
            }`}
            style={{ minWidth: `${cols}ch` }}
        />
    );
}

// Round 16 · Bank Splits Chip.
// A small "N accts" indicator next to the Net cell. Click opens a
// popover listing every bank account on the master, one editable
// fixed-dollar amount per split account, and a computed remainder row
// showing what will land in the default account when the DE file is
// generated. The chip is HIDDEN on employees with fewer than two bank
// accounts (nothing to split) so the pay-run row stays quiet for the
// common case. Bank-payable per Michael's rule is net − travel; the
// popover echoes it so the operator can see whether a split will fit.
function BankSplitsChip({ line, employee, readOnly, bankPayable, onChange }) {
    const accounts = (employee?.bank_accounts) || [];
    if (accounts.length < 2) return null;
    const defaultAcc = accounts.find(a => a.is_default) || accounts[0];
    const nonDefault = accounts.filter(a => a.id !== defaultAcc.id);
    const splits = line.bank_splits || [];
    const splitByAcc = Object.fromEntries(splits.map(s => [s.account_id, Number(s.amount_kina || 0)]));
    const splitSum = Object.values(splitByAcc).reduce((a, b) => a + b, 0);
    const remainder = Math.max(0, Number(bankPayable || 0) - splitSum);
    const over = splitSum > Number(bankPayable || 0);
    const activeCount = splits.length + (remainder > 0 ? 1 : 0);
    const updateAmount = (accId, amount) => {
        const clean = accounts
            .filter(a => a.id !== defaultAcc.id)
            .map(a => ({ account_id: a.id, amount_kina: a.id === accId ? amount : (splitByAcc[a.id] || 0) }))
            .filter(s => s.amount_kina > 0);
        onChange(line.id, clean);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={PAYRUN.splitsChip(line.id)}
                    className="inline-flex items-center gap-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-[2px] border"
                    style={{
                        color: over ? "var(--kp-red)" : "var(--kp-muted)",
                        borderColor: over ? "var(--kp-red)" : "#E5E5E7",
                    }}
                    title={`${activeCount} bank account${activeCount === 1 ? "" : "s"} for this run`}
                >
                    {activeCount} acct{activeCount === 1 ? "" : "s"}
                    <ChevronDown size={10} className="opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={4}
                data-testid={PAYRUN.splitsPopover(line.id)}
                className="w-[320px] p-3 bg-white border border-neutral-200 shadow-lg rounded-[3px]"
            >
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-2 border-b border-neutral-200 pb-1.5 flex items-baseline justify-between">
                    <span>Splits · {employee?.name || "—"}</span>
                    <span className="font-mono-money text-[10px] text-neutral-400">
                        payable {formatKina(bankPayable)}
                    </span>
                </div>
                {nonDefault.map(a => (
                    <div key={a.id} className="flex justify-between items-center py-1">
                        <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-neutral-700 truncate">
                                {a.nickname || a.account_title || a.account_number}
                            </div>
                            <div className="text-[9px] text-neutral-400 uppercase tracking-[0.14em] font-display">
                                {a.bsb} · {a.account_number}
                            </div>
                        </div>
                        {readOnly ? (
                            <div className="font-mono-money text-[13px] text-neutral-700 px-2 py-1">
                                {formatKina(splitByAcc[a.id] || 0)}
                            </div>
                        ) : (
                            <input
                                data-testid={PAYRUN.splitsAmount(line.id, a.id)}
                                type="number"
                                step="0.01"
                                min="0"
                                value={splitByAcc[a.id] || 0}
                                onChange={(e) => updateAmount(a.id, e.target.valueAsNumber || 0)}
                                onFocus={(e) => e.target.select()}
                                className="kp-focus w-[100px] font-mono-money px-2 py-1 text-[13px] text-right bg-transparent border border-neutral-200 focus:border-[color:var(--kp-red)] focus:bg-white rounded-[2px]"
                            />
                        )}
                    </div>
                ))}
                <div
                    data-testid={PAYRUN.splitsRemainderRow(line.id)}
                    className="flex justify-between items-center pt-2 mt-2 border-t border-neutral-200"
                >
                    <div>
                        <div className="text-[12px] text-neutral-700">
                            {defaultAcc.nickname || defaultAcc.account_title || defaultAcc.account_number}
                        </div>
                        <div className="text-[9px] text-neutral-400 uppercase tracking-[0.14em] font-display">
                            default · gets remainder
                        </div>
                    </div>
                    <div className="font-mono-money text-[13px] font-semibold"
                         style={{ color: over ? "var(--kp-red)" : "var(--kp-charcoal)" }}>
                        {formatKina(remainder)}
                    </div>
                </div>
                {over && (
                    <div className="mt-2 text-[10px]" style={{ color: "var(--kp-red)" }}>
                        Splits exceed payable — this employee will be EXCLUDED from the Westpac DE file.
                    </div>
                )}
                {!readOnly && splits.length > 0 && (
                    <button
                        type="button"
                        data-testid={PAYRUN.splitsClearBtn(line.id)}
                        onClick={() => onChange(line.id, [])}
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-500 border border-neutral-200 rounded-[3px] hover:border-[color:var(--kp-red)] hover:text-[color:var(--kp-red)]"
                    >
                        Clear splits · pay 100% to default
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}

// Locked cells render as plain right-aligned text (no input chrome) so the historical
// record reads like a document, not an editable form. Zeros stay legible (K0.00).
function ReadCell({ value, testid, hours = false }) {
    const display = hours ? formatHours(value ?? 0) : formatKina(value ?? 0);
    return (
        <div data-testid={testid} className="w-full text-right font-mono-money px-2 py-1.5 text-[13px] text-neutral-700">
            {display}
        </div>
    );
}

// B3 · Allowances Chip.
// One cell surface in the pay-line table showing the SUM of the five
// recurring allowance categories (bus + school + travel + housing + other).
// Click opens a popover with all five categories laid out row-by-row:
//   * Bus + School fee are editable — writes to `bus_allowance_override`
//     and `school_fee_override` on the pay-line. `null` means fall back
//     to the master, non-null (including zero) wins for this run only.
//   * Travel / Housing / Other are read from the employee master and
//     are read-only here. A single "Set on employee →" button opens the
//     employee drawer so the operator can edit the master value without
//     leaving the pay-run screen.
//   * The one-off `allowance_kina` input stays inside the popover so
//     Michael has one place for every K figure that hits the row.
// The chip's visible total INCLUDES the one-off — showing an operator
// only 5 of the 6 numbers that end up on the row would be worse than
// technically-correct. Spec says the total "includes all 5"; adding the
// one-off doesn't violate the floor.
function AllowanceChip({ line, employee, readOnly, onBus, onSchool, onOneOff, onEditEmployee }) {
    const ra = employee?.recurring_allowances || {};
    const masterBus = Number(ra.bus_kina || 0);
    const masterSchool = Number(ra.school_fee_kina || 0);
    const travel = Number(ra.travel_kina || 0);
    const housing = Number(ra.housing_kina || 0);
    const other = Number(ra.other_kina || 0);
    const busVal = line.bus_allowance_override != null ? Number(line.bus_allowance_override) : masterBus;
    const schoolVal = line.school_fee_override != null ? Number(line.school_fee_override) : masterSchool;
    const oneOff = Number(line.allowance_kina || 0);
    const total = busVal + schoolVal + travel + housing + other + oneOff;
    const lineId = line.id;

    // Small helper — the chip surface. Same look editable vs locked.
    const trigger = (
        <button
            type="button"
            data-testid={PAYRUN.allowChip(lineId)}
            className="inline-flex items-center gap-1 font-mono-money text-[13px] text-neutral-700 hover:text-[color:var(--kp-red)] w-full justify-end px-2 py-1.5"
            title="Allowances breakdown"
        >
            <span data-testid={PAYRUN.allowChipTotal(lineId)}>{formatKina(total)}</span>
            <ChevronDown size={12} className="opacity-60" />
        </button>
    );

    return (
        <Popover>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={4}
                data-testid={PAYRUN.allowPopover(lineId)}
                className="w-[280px] p-3 bg-white border border-neutral-200 shadow-lg rounded-[3px]"
            >
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-2 border-b border-neutral-200 pb-1.5">
                    Allowances · {employee?.name || "—"}
                </div>

                {/* Editable per-line — Bus */}
                <ChipRow
                    label="Bus"
                    sub={line.bus_allowance_override != null ? "override" : "from master"}
                    readOnly={readOnly}
                    testid={PAYRUN.allowBusInput(lineId)}
                    value={busVal}
                    onChange={(v) => onBus(lineId, v)}
                />
                {/* Editable per-line — School fee */}
                <ChipRow
                    label="School fee"
                    sub={line.school_fee_override != null ? "override" : "from master"}
                    readOnly={readOnly}
                    testid={PAYRUN.allowSchoolInput(lineId)}
                    value={schoolVal}
                    onChange={(v) => onSchool(lineId, v)}
                />
                {/* Read-only from employee master */}
                <ChipRow label="Travel" sub="from master" readOnly value={travel} testid={PAYRUN.allowTravelRead(lineId)} />
                <ChipRow label="Housing" sub="from master" readOnly value={housing} testid={PAYRUN.allowHousingRead(lineId)} />
                <ChipRow label="Other" sub="from master" readOnly value={other} testid={PAYRUN.allowOtherRead(lineId)} />

                {/* One-off — editable per-line via existing allowance_kina */}
                <ChipRow
                    label="One-off K"
                    sub="this run only"
                    readOnly={readOnly}
                    testid={PAYRUN.allowOneOffInput(lineId)}
                    value={oneOff}
                    onChange={(v) => onOneOff(lineId, v)}
                />

                <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-neutral-200">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Total</div>
                    <div
                        data-testid={PAYRUN.allowPopoverTotal(lineId)}
                        className="font-mono-money font-semibold text-[14px] text-[color:var(--kp-charcoal)]"
                    >
                        {formatKina(total)}
                    </div>
                </div>

                {!readOnly && employee && (
                    <button
                        type="button"
                        data-testid={PAYRUN.allowEditEmployeeBtn(lineId)}
                        onClick={() => onEditEmployee(employee)}
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                        style={{ background: "var(--kp-red)" }}
                    >
                        Set travel / housing / other on employee <ExternalLink size={12} />
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}

// Round 14 · Negative-leave warning. Warn — never block save — when
// this line's AL or sick hours would drop the projected leave balance
// below zero. `current.from_cut_in_only=true` means opening was never
// supplied on the master, so "balance" is meaningless and no warning
// fires. Reads the enriched `leave_current.{annual,sick}.balance` the
// server attaches to every pay-run line.
function LeaveOverWarning({ testid, hours, current }) {
    const h = Number(hours || 0);
    if (h <= 0 || !current || current.from_cut_in_only) return null;
    const bal = Number(current.balance || 0);
    const projected = bal - h;
    if (projected >= 0) return null;
    return (
        <div
            data-testid={testid}
            title={`Available ${bal.toFixed(2)}h · this run would push it to ${projected.toFixed(2)}h. Warning only — save is not blocked.`}
            className="text-[10px] font-mono-money text-right mt-0.5"
            style={{ color: "var(--kp-red)" }}
        >
            ⚠ {projected.toFixed(2)}h
        </div>
    );
}

function ChipRow({ label, sub, value, onChange, readOnly, testid }) {    return (
        <div className="flex justify-between items-center py-1">
            <div>
                <div className="text-[12px] text-neutral-700">{label}</div>
                {sub && <div className="text-[9px] text-neutral-400 uppercase tracking-[0.14em] font-display">{sub}</div>}
            </div>
            {readOnly || !onChange ? (
                <div data-testid={testid} className="font-mono-money text-[13px] text-neutral-700 px-2 py-1">
                    {formatKina(value)}
                </div>
            ) : (
                <input
                    data-testid={testid}
                    type="number"
                    step="0.01"
                    min="0"
                    value={value ?? 0}
                    onChange={(e) => onChange(e.target.valueAsNumber || 0)}
                    onFocus={(e) => e.target.select()}
                    className="kp-focus w-[100px] font-mono-money px-2 py-1 text-[13px] text-right bg-transparent border border-neutral-200 focus:border-[color:var(--kp-red)] focus:bg-white rounded-[2px]"
                />
            )}
        </div>
    );
}

export default function PayRun() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isUploader = user?.role === "uploader";
    const [period, setPeriod] = useState(null);
    const [lines, setLines] = useState([]);
    const [importOpen, setImportOpen] = useState(false);
    const [bioOpen, setBioOpen] = useState(false);
    const [skippedOpen, setSkippedOpen] = useState(false);
    const [detail, setDetail] = useState(null); // pay line
    const [editEmp, setEditEmp] = useState(null); // employee obj
    const [historyOpen, setHistoryOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/pay-periods/${id}`);
            setPeriod(data.period);
            setLines(data.lines);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Pay period not found");
            navigate("/pay-runs", { replace: true });
        }
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line

    const totals = period?.totals || { gross: 0, tax: 0, net: 0 };
    // Headcount = active lines only. Inactive employees stay on the run so the
    // reviewer sees they were on it, but they are excluded from the paid tally,
    // the payslip zip and the bank CSV (see server-side exports).
    const activeStaff = lines.filter((ln) => ln.active !== false).length;
    const staff = activeStaff;
    const totalLines = lines.length;
    const inactiveStaff = totalLines - activeStaff;
    const isLocked = !!period && period.status !== "draft";
    const readOnly = !isAdmin || isLocked;
    const skippedRows = period?.import_skipped || [];

    // Optimistic patch → server, then refresh totals from response
    const patchLine = async (lineId, patch) => {
        // optimistic UI
        setLines((prev) =>
            prev.map((ln) => (ln.id === lineId ? { ...ln, ...patch, hours: patch.hours ? { ...ln.hours, ...patch.hours } : ln.hours } : ln)),
        );
        try {
            const { data } = await api.patch(`/pay-periods/${id}/lines/${lineId}`, patch);
            setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, ...data.line, employee: ln.employee } : ln)));
            setPeriod((p) => ({ ...p, totals: data.totals }));
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
            load(); // rollback
        }
    };
    const debouncedPatch = useCoalescedLinePatch(patchLine, 300);

    const setHours = (lineId, field, value) => {
        setLines((prev) => prev.map((ln) =>
            ln.id === lineId ? { ...ln, hours: { ...ln.hours, [field]: value } } : ln,
        ));
        debouncedPatch(lineId, { hours: { [field]: value } });
    };
    const setRate = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, rate_override: value } : ln)));
        debouncedPatch(lineId, { rate_override: value });
    };
    const setDeps = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, dependants_override: value } : ln)));
        debouncedPatch(lineId, { dependants_override: value });
    };
    const setAllow = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, allowance_kina: value } : ln)));
        debouncedPatch(lineId, { allowance_kina: value });
    };
    // B3 · Allowances Chip — bus / school fee are the two recurring
    // categories editable per line. `null` on either override means "no
    // override — fall back to the master value" and the server $sets
    // through unchanged so the operator can reset an override without
    // touching the master. See test_payline_allowance_overrides.py.
    const setBusOverride = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, bus_allowance_override: value } : ln)));
        debouncedPatch(lineId, { bus_allowance_override: value });
    };
    const setSchoolOverride = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, school_fee_override: value } : ln)));
        debouncedPatch(lineId, { school_fee_override: value });
    };
    // Round 16 · bank splits replace atomically. `null` (leave alone)
    // never reaches the API from the chip — the chip always sends the
    // full list or `[]` to clear.
    const setBankSplits = (lineId, splits) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, bank_splits: splits } : ln)));
        debouncedPatch(lineId, { bank_splits: splits });
    };
    const setLoan = (lineId, value) => {
        setLines((prev) => prev.map((ln) => (ln.id === lineId ? { ...ln, loan_repayment_kina: value } : ln)));
        debouncedPatch(lineId, { loan_repayment_kina: value });
    };

    const canApprove = useMemo(() => {
        if (!lines.length) return false;
        for (const ln of lines) {
            if (ln.active === false) continue; // inactive rows aren't paid — don't gate on their rate
            const emp = ln.employee || {};
            const rate = ln.rate_override != null ? ln.rate_override : emp.labour_rate_kina || 0;
            const anyHours = ["regular", "time_and_half", "double", "sick", "ph_worked", "ph_taken", "annual_leave_taken"]
                .some((k) => Number(ln.hours?.[k] || 0) > 0);
            if (anyHours && !(rate > 0)) return false;
        }
        return true;
    }, [lines]);

    if (!period) {
        return (
            <div className="p-10 text-neutral-500 text-sm">Loading pay run…</div>
        );
    }

    const approvedShort = (period.approved_at || "").slice(0, 10);

    return (
        <div data-testid={PAYRUN.page} className="px-3 py-5 md:px-8 md:py-8 max-w-[1600px] min-w-0">
            {/* Top row: PPE + workflow + actions.
                min-w-0 + flex-wrap so a narrow phone viewport does not force the
                whole page to scroll horizontally — the ONLY thing that scrolls
                sideways on this screen is .payrun-scroll (the middle of the
                pay-line table), and that has to hold at 390px so the sticky
                Employee / Gross / Tax / Net pinning is meaningful. `body`
                overflowing horizontally makes sticky useless because sticky
                is relative to the scroll container. */}
            <div className="flex items-start justify-between mb-3 flex-wrap gap-3 min-w-0">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                            Pay run · {period.status === "draft" ? "Draft" : period.status.toUpperCase()}
                        </div>
                        {isLocked && (
                            <span
                                data-testid={PAYRUN.lockedChip}
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                style={{ background: "#E4E7EB", color: "#3D4A5C" }}
                            >
                                <Lock size={11} /> Locked — approved {approvedShort}
                            </span>
                        )}
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        PPE <span className="font-mono-money">{period.ppe_date}</span>
                    </h1>
                    <div className="text-xs text-neutral-500 mt-1 font-mono-money">
                        {period.start_date} → {period.end_date}
                    </div>
                    {skippedRows.length > 0 && (
                        <button
                            data-testid={PAYRUN.skippedRowsLink}
                            onClick={() => setSkippedOpen(true)}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-neutral-600 hover:text-[color:var(--kp-red)] underline underline-offset-2 decoration-dotted"
                        >
                            <SkipForward size={12} />
                            {skippedRows.length} row{skippedRows.length === 1 ? "" : "s"} skipped on import — view
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin && (
                        <button
                            data-testid="payrun-history-btn"
                            onClick={() => setHistoryOpen(true)}
                            title="Audit log: biometric uploads, reopen acknowledgements, S2 lodgement events"
                            className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 hover:border-neutral-500 rounded-[3px]"
                        >
                            History
                        </button>
                    )}
                    {!readOnly && (
                        <button
                            onClick={() => setBioOpen(true)}
                            title="Ingest the raw KALMARPEI biometric export and see the calculated buckets + exceptions"
                            className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 hover:border-neutral-500 rounded-[3px]"
                        >
                            <Wifi size={14} /> Biometric raw
                        </button>
                    )}
                    {!readOnly && (
                        <button
                            data-testid={PAYRUN.importBtn}
                            onClick={() => setImportOpen(true)}
                            className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-[color:var(--kp-charcoal)] text-[color:var(--kp-charcoal)] hover:bg-[color:var(--kp-charcoal)] hover:text-white rounded-[3px]"
                        >
                            <Upload size={14} /> Import hours
                        </button>
                    )}
                    {!readOnly && (
                        <button
                            data-testid={PAYRUN.approveBtn}
                            onClick={() => navigate(`/pay-runs/${id}/approve`)}
                            disabled={!canApprove}
                            title={canApprove ? "Review & approve" : "One or more rows have hours but no labour rate"}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                            style={{ background: "var(--kp-red)" }}
                        >
                            Review &amp; approve <ArrowRight size={14} />
                        </button>
                    )}
                    {isLocked && (
                        <button
                            onClick={() => navigate(`/pay-runs/${id}/complete`)}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                            style={{ background: "var(--kp-charcoal)" }}
                        >
                            Downloads &amp; exports <ArrowRight size={14} />
                        </button>
                    )}
                </div>
            </div>

            <WorkflowStrip active={period.status === "draft" ? 3 : 4} />

            {period.status === "draft" && isAdmin && (
                <PendingBiometricBanner
                    period={period}
                    onCleared={() => load()}
                />
            )}

            <HistoryDrawer pid={id} open={historyOpen} onClose={() => setHistoryOpen(false)} />

            {!isUploader && (
                <WindowExceptionsStrip pid={id} isUploader={isUploader} isAdmin={isAdmin}
                                       isDraft={period.status === "draft"} onChanged={load} />
            )}

            {/* KPI strip */}
            <div className="grid grid-cols-4 gap-4 mb-5">
                <Kpi tid={PAYRUN.kpiStaff} label="Staff on run" value={staff} mono={false} />
                <Kpi tid={PAYRUN.kpiGross} label="Total gross" value={formatKina(totals.gross)} />
                <Kpi tid={PAYRUN.kpiTax} label="Tax to remit" value={formatKina(totals.tax)} color="var(--kp-red)" accent />
                <Kpi tid={PAYRUN.kpiNet} label="Net to pay" value={formatKina(totals.net)} color="var(--kp-success)" accent accentColor="var(--kp-success)" />
            </div>

            {inactiveStaff > 0 && (
                <div
                    data-testid="payrun-inactive-banner"
                    className="mb-4 p-3 border-l-[3px] text-[13px] rounded-[3px]"
                    style={{ background: "#FEF6E6", borderLeftColor: "#C88A17", color: "#5A3A00" }}
                >
                    <span className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] mr-2" style={{ color: "#8A5A00" }}>
                        {inactiveStaff} inactive
                    </span>
                    {inactiveStaff === 1 ? "one worker" : `${inactiveStaff} workers`} on this run {inactiveStaff === 1 ? "is" : "are"} marked INACTIVE on the master and will not be paid — no payslip, no bank line, no contribution to IRC. Rows shown for audit.
                </div>
            )}

            {!readOnly && <SickPHBanner />}

            {/* Table */}
            <div className="bg-white border border-neutral-200 rounded-[3px]">
                <div
                    className="overflow-x-auto payrun-scroll"
                    data-testid="payrun-scroll"
                    ref={(el) => {
                        if (!el || el._payrunScrollWired) return;
                        el._payrunScrollWired = true;
                        const sync = () => {
                            const max = el.scrollWidth - el.clientWidth;
                            el.setAttribute("data-scrolled-left", String(el.scrollLeft > 0));
                            el.setAttribute("data-scrolled-right", String(el.scrollLeft < max - 1));
                        };
                        sync();
                        el.addEventListener("scroll", sync, { passive: true });
                        window.addEventListener("resize", sync);
                    }}
                >
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                <th className="stk-emp px-4 py-3 font-semibold">Employee</th>
                                <th className="px-3 py-3 font-semibold text-right">Rate K/hr</th>
                                <th className="px-2 py-3 font-semibold text-center">Dep</th>
                                <th className="px-2 py-3 font-semibold text-right">Reg</th>
                                <th className="px-2 py-3 font-semibold text-right">×1.5</th>
                                <th className="px-2 py-3 font-semibold text-right">×2.0</th>
                                <th className="px-2 py-3 font-semibold text-right">Sick</th>
                                <th className="px-2 py-3 font-semibold text-right" title="Public holiday WORKED — paid at ×2.0 (Mike, 4 Sept 2026)">PH Wkd ×2</th>
                                <th className="px-2 py-3 font-semibold text-right" title="Public holiday TAKEN — day off, paid at ordinary rate">PH Tkn</th>
                                <th className="px-2 py-3 font-semibold text-right" title="Annual leave TAKEN — paid at ordinary rate, still qualifies for accrual">AL Tkn</th>
                                <th className="px-2 py-3 font-semibold text-right" title="Long-service leave TAKEN — paid at ordinary rate, hits taxable gross, does NOT accrue AL/SL">LSL</th>
                                <th className="px-2 py-3 font-semibold text-right">Allow K</th>
                                <th className="stk-gross px-3 py-3 font-semibold text-right">Gross</th>
                                <th className="stk-tax px-3 py-3 font-semibold text-right">Tax</th>
                                {isAdmin && <th data-testid={PAYRUN_LOAN.header} className="px-3 py-3 font-semibold text-right">Loan</th>}
                                <th className="stk-net px-3 py-3 font-semibold text-right">Net</th>
                                <th className="px-2 py-3 font-semibold text-center">✎</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((ln) => {
                                const emp = ln.employee || {};
                                const isInactive = ln.active === false;
                                const rate = ln.rate_override != null ? ln.rate_override : emp.labour_rate_kina || 0;
                                const deps = ln.dependants_override != null ? ln.dependants_override : (emp.dependants_declared ?? emp.dependants ?? 0);
                                const c = ln.calc || {};
                                const rowMissingRate =
                                    !readOnly && !isInactive &&
                                    ["regular", "time_and_half", "double", "sick", "ph_worked", "ph_taken", "annual_leave_taken"].some((k) => Number(ln.hours?.[k] || 0) > 0) &&
                                    !(rate > 0);
                                const rowStyle = isInactive
                                    ? { background: "#F8F8F9", color: "#8B8B90" }
                                    : undefined;
                                return (
                                    <tr key={ln.id} data-testid={PAYRUN.row(ln.id)} className={`border-t border-neutral-100 ${rowMissingRate ? "bg-red-50/50" : ""} ${isInactive ? "opacity-60" : ""}`} style={{ background: rowStyle?.background || "#fff", color: rowStyle?.color }}>
                                        <td className="stk-emp px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <div data-testid={PAYRUN.rowName(ln.id)} className="font-medium text-[color:var(--kp-charcoal)]">{emp.name}</div>
                                                {isInactive && (
                                                    <span
                                                        data-testid={`payrun-row-inactive-badge-${ln.id}`}
                                                        title="This employee is inactive on the master. They stay on the run for audit but will not be paid — excluded from payslip zip, bank CSV, and IRC remittance."
                                                        className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-display font-semibold rounded-[2px]"
                                                        style={{ background: "#C88A17", color: "#fff" }}
                                                    >
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-neutral-400">{emp.position || "—"}</div>
                                        </td>
                                        {readOnly ? (
                                            <>
                                                <td className="px-3 py-2 w-[110px]"><ReadCell testid={PAYRUN.cellRate(ln.id)} value={rate} /></td>
                                                <td data-testid={PAYRUN.cellDeps(ln.id)} className="px-2 py-2 w-[70px] text-center font-mono-money text-neutral-700 text-[13px]">
                                                    {deps === 3 ? "3+" : deps}
                                                </td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellReg(ln.id)} value={ln.hours?.regular} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellTah(ln.id)} value={ln.hours?.time_and_half} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellDbl(ln.id)} value={ln.hours?.double} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellSick(ln.id)} value={ln.hours?.sick} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellPh(ln.id)} value={ln.hours?.ph_worked ?? ln.hours?.public_holiday} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellPhTaken(ln.id)} value={ln.hours?.ph_taken} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellAlTaken(ln.id)} value={ln.hours?.annual_leave_taken} hours /></td>
                                                <td className="px-2 py-2 w-[80px]"><ReadCell testid={PAYRUN.cellLsl(ln.id)} value={ln.hours?.long_service_taken} hours /></td>
                                                <td className="px-2 py-2 w-[130px]" data-testid={PAYRUN.cellAllow(ln.id)}>
                                                    <AllowanceChip
                                                        line={ln}
                                                        employee={emp}
                                                        readOnly
                                                        onBus={setBusOverride}
                                                        onSchool={setSchoolOverride}
                                                        onOneOff={setAllow}
                                                        onEditEmployee={setEditEmp}
                                                    />
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-3 py-2 w-[110px]">
                                                    <NumCell testid={PAYRUN.cellRate(ln.id)} value={rate} onChange={(v) => setRate(ln.id, v)} cols={7} />
                                                </td>
                                                <td className="px-2 py-2 w-[70px]">
                                                    <select
                                                        data-testid={PAYRUN.cellDeps(ln.id)}
                                                        value={deps}
                                                        onChange={(e) => setDeps(ln.id, parseInt(e.target.value, 10))}
                                                        className="kp-focus w-full font-mono-money px-1.5 py-1.5 text-[13px] bg-transparent border border-transparent hover:border-neutral-200 focus:border-[color:var(--kp-red)] focus:bg-white text-center rounded-[2px]"
                                                    >
                                                        {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n === 3 ? "3+" : n}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellReg(ln.id)} value={ln.hours?.regular} onChange={(v) => setHours(ln.id, "regular", v)} /></td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellTah(ln.id)} value={ln.hours?.time_and_half} onChange={(v) => setHours(ln.id, "time_and_half", v)} /></td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellDbl(ln.id)} value={ln.hours?.double} onChange={(v) => setHours(ln.id, "double", v)} /></td>
                                                <td className="px-2 py-2 w-[80px]">
                                                    <NumCell testid={PAYRUN.cellSick(ln.id)} value={ln.hours?.sick} onChange={(v) => setHours(ln.id, "sick", v)} />
                                                    <LeaveOverWarning
                                                        testid={PAYRUN.cellSickWarn(ln.id)}
                                                        hours={ln.hours?.sick}
                                                        current={ln.leave_current?.sick}
                                                    />
                                                </td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellPh(ln.id)} value={ln.hours?.ph_worked ?? ln.hours?.public_holiday ?? 0} onChange={(v) => setHours(ln.id, "ph_worked", v)} /></td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellPhTaken(ln.id)} value={ln.hours?.ph_taken ?? 0} onChange={(v) => setHours(ln.id, "ph_taken", v)} /></td>
                                                <td className="px-2 py-2 w-[80px]">
                                                    <NumCell testid={PAYRUN.cellAlTaken(ln.id)} value={ln.hours?.annual_leave_taken ?? 0} onChange={(v) => setHours(ln.id, "annual_leave_taken", v)} />
                                                    <LeaveOverWarning
                                                        testid={PAYRUN.cellAlWarn(ln.id)}
                                                        hours={ln.hours?.annual_leave_taken}
                                                        current={ln.leave_current?.annual}
                                                    />
                                                </td>
                                                <td className="px-2 py-2 w-[80px]"><NumCell testid={PAYRUN.cellLsl(ln.id)} value={ln.hours?.long_service_taken ?? 0} onChange={(v) => setHours(ln.id, "long_service_taken", v)} /></td>
                                                <td className="px-2 py-2 w-[130px]" data-testid={PAYRUN.cellAllow(ln.id)}>
                                                    <AllowanceChip
                                                        line={ln}
                                                        employee={emp}
                                                        readOnly={false}
                                                        onBus={setBusOverride}
                                                        onSchool={setSchoolOverride}
                                                        onOneOff={setAllow}
                                                        onEditEmployee={setEditEmp}
                                                    />
                                                </td>
                                            </>
                                        )}
                                        <td data-testid={PAYRUN.cellGross(ln.id)} className="stk-gross px-3 py-2 text-right font-mono-money">{formatKina(c.taxable_gross)}</td>
                                        <td data-testid={PAYRUN.cellTax(ln.id)} className="stk-tax px-3 py-2 text-right font-mono-money" style={{ color: "var(--kp-red)" }}>{formatKina(c.fortnightly_tax)}</td>
                                        {isAdmin && (
                                            <td data-testid={PAYRUN_LOAN.cell(ln.id)} className="px-3 py-2 text-right w-[110px]">
                                                {readOnly ? (
                                                    <span className="font-mono-money text-neutral-700">{formatKina(c.loan_repayment || 0)}</span>
                                                ) : (
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <input
                                                            data-testid={PAYRUN_LOAN.input(ln.id)}
                                                            type="number" step="0.01" min="0"
                                                            value={ln.loan_repayment_kina ?? 0}
                                                            onChange={(e) => setLoan(ln.id, Number(e.target.valueAsNumber) || 0)}
                                                            className="kp-focus w-[90px] font-mono-money px-1.5 py-1 text-[12px] border border-transparent hover:border-neutral-200 focus:border-[color:var(--kp-red)] focus:bg-white text-right rounded-[2px]"
                                                        />
                                                        {(() => {
                                                            const gross = Number(c.taxable_gross || 0);
                                                            const tax = Number(c.fortnightly_tax || 0);
                                                            const netBeforeLoan = Math.max(0, gross - tax);
                                                            const loan = Number(ln.loan_repayment_kina || 0);
                                                            if (loan > 0 && loan > netBeforeLoan * 0.5) {
                                                                return (
                                                                    <span
                                                                        data-testid={PAYRUN_LOAN.warning(ln.id)}
                                                                        title={`Repayment ${loan.toFixed(2)} exceeds half of net-before-loan (${(netBeforeLoan/2).toFixed(2)}). Sanity check — no Labour Act cap set yet.`}
                                                                        className="text-amber-600"
                                                                    >⚠</span>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        <td data-testid={PAYRUN.cellNet(ln.id)} className="stk-net px-3 py-2 text-right font-mono-money font-semibold" style={{ color: "var(--kp-success)" }}>
                                            <div className="flex items-center justify-end gap-1.5">
                                                <BankSplitsChip
                                                    line={ln}
                                                    employee={emp}
                                                    readOnly={period.status !== "draft"}
                                                    bankPayable={(c.net_pay || 0) - (ln.travel_net_kina || 0)}
                                                    onChange={setBankSplits}
                                                />
                                                <span>{formatKina(c.net_pay)}</span>
                                            </div>
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                            <button data-testid={PAYRUN.rowDetailBtn(ln.id)} onClick={() => setDetail(ln)} title="Show tax working" className="text-neutral-400 hover:text-[color:var(--kp-red)] text-[13px]">
                                                ✎
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>

                        {/* Charcoal footer */}
                        <tfoot>
                            <tr style={{ background: "var(--kp-charcoal)", color: "#fff" }}>
                                <td className="stk-emp px-4 py-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-display">Totals</div>
                                    <div data-testid="payrun-footer-staff" className="font-display font-bold text-lg mt-0.5">
                                        {staff} staff
                                        {inactiveStaff > 0 && (
                                            <span className="ml-2 text-[11px] font-normal tracking-normal" style={{ color: "#F0A040" }}>
                                                (of {totalLines} · {inactiveStaff} inactive excluded)
                                            </span>
                                        )}
                                    </div>
                                </td>
                                {/* colSpan advances past: Rate, Dep, Reg, ×1.5, ×2.0, Sick, PH Wkd, PH Tkn, AL Tkn, LSL, Allow K  (11) */}
                                <td colSpan={9} />
                                <td data-testid={PAYRUN.footerGross} className="stk-gross px-3 py-4 text-right font-display font-bold text-xl">
                                    {formatKina(totals.gross)}
                                </td>
                                <td data-testid={PAYRUN.footerTax} className="stk-tax px-3 py-4 text-right font-display font-bold text-xl" style={{ color: "#FF6B75" }}>
                                    {formatKina(totals.tax)}
                                </td>
                                {isAdmin && <td />}
                                <td data-testid={PAYRUN.footerNet} className="stk-net px-3 py-4 text-right font-display font-bold text-xl" style={{ color: "#59C783" }}>
                                    {formatKina(totals.net)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <ImportHoursModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                periodId={id}
                onApplied={() => { setImportOpen(false); load(); }}
            />
            <BiometricPreviewModal
                open={bioOpen}
                onClose={() => setBioOpen(false)}
                periodId={id}
                onApplied={() => { setBioOpen(false); load(); }}
            />
            <SkippedRowsModal
                open={skippedOpen}
                onClose={() => setSkippedOpen(false)}
                rows={skippedRows}
            />
            <PayLineDrawer
                open={!!detail}
                line={detail}
                onClose={() => setDetail(null)}
                onEditProfile={(emp) => { setDetail(null); setEditEmp(emp); }}
            />
            <EmployeeDrawer
                open={!!editEmp}
                employee={editEmp}
                onClose={() => setEditEmp(null)}
                onSaved={() => { setEditEmp(null); load(); }}
            />
        </div>
    );
}

function Kpi({ tid, label, value, color, accent, accentColor }) {
    return (
        <div className="border border-neutral-200 bg-white p-4 rounded-[3px]" style={accent ? { borderTop: `3px solid ${accentColor || "var(--kp-red)"}` } : {}}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">{label}</div>
            <div data-testid={tid} className="font-display font-bold text-3xl mt-1 font-mono-money" style={{ color: color || "var(--kp-charcoal)" }}>
                {value}
            </div>
        </div>
    );
}
